import React, { useEffect, useState } from "react";
import { SerializedError } from "@reduxjs/toolkit";
import { useParams, useNavigate, generatePath } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../framework/redux/hooks";
import {
    TransportOrderDetailsDto,
    TransportOrderState,
} from "../../../models/transport/transportOrder";
import { LoadingIndicator } from "../../framework/loadingIndicator";
import { OrderDetails } from "./orderDetails";
import { DriverComments } from "./driverComments";
import { OrderType } from "./orderType";
import { MainLayout } from "../../layout/mainLayout";
import { PageTitle } from "../../layout/pageTitle";
import { Button, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TransportDetails } from "./transportDetails";
import { getOrderDetails, updateEditingOrder, saveTransportOrderWithNotification, setTransportOrderDetails, saveTransportOrderEmployee, removeTransportOrder } from "../../../framework/redux/slices/transportOrderDetailsSlice";
import { addErrorMsg, addSuccessMsg } from "../../../framework/redux/slices/appSlice";
import { ProductTable } from "./productTable/productTable";
import { TransportOrderInvoicingState } from "../../../models/common/enums";
import { useAsyncThunkAction } from "../../../hooks/framework/useAsyncThunkAction";
import { fetchCurrentWorkShift } from "../../../framework/redux/slices/workShiftSlice";
import { handleApiError } from "../../../services/baseService";
import { OrderStateToggle } from "./orderStateToggle";
import { confirmDesktopToMobileNotifications } from "../../../services/transportOrderNotificationService";
import { TransportOrderNotificationState } from "../../../models/transport/transportOrderNotification";
import { HandleSaveProps, toUpdateDto } from "./transportOrderUtils";
import { useSelectNotificationsForTransportOrder } from "../../../hooks/notification/useSelectNotificationsTransportOrder";
import { LinkIconButton } from "../../../framework/common/linkButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { ConfirmType, useConfirm } from "../../../hooks/framework/useConfirm";
import { OrderAttachments } from "./orderAttachments";

export const TransportOrder = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const confirm = useConfirm();

    const order = useAppSelector((state) => state.transportOrderDetails.order);
    const editOrder = useAppSelector((state) => state.transportOrderDetails.editingOrder);

    const { orderId } = useParams();
    const [localOrderState, setLocalOrderState] = React.useState<TransportOrderState | null>(null);
    const employeeId = useAppSelector((state) => state.session.id);
    const { t } = useTranslation();
    const [bottomSpace, setBottomSpace] = React.useState<number>(72);

    const canCreateWorkOrderInMobile = useAppSelector(
        ({ session }) => session.employeeGroupParameters?.canCreateWorkOrderInMobile
    );

    useEffect(() => {
        // Reset order to avoid that previously opened order and its data is shown
        dispatch(setTransportOrderDetails(null));
    }, []);

    useEffect(() => {
        if (order?.state !== undefined) setLocalOrderState(order.state);
    }, [order?.state]);

    useAsyncThunkAction(
        () => fetchCurrentWorkShift(),
        { onError: handleApiError },
        []
    );

    useEffect(() => {
        if (orderId) {
            const promise = dispatch(getOrderDetails({ orderId }));
            promise.unwrap().catch((e: SerializedError) => {
                if (e?.name !== "AbortError") navigate("/");
            });
            return () => {
                promise.abort();
            };
        }
    }, [orderId, dispatch, navigate]);

    useEffect(() => {
        if (order && order?.id === orderId) {
            setLoading(false);
        } else {
            setLoading(true);
        }
    }, [order, orderId]);

    const editDisabledState = localOrderState !== null && localOrderState >= TransportOrderState.Cancelled;
    const notifications = useSelectNotificationsForTransportOrder(orderId);
    // If there are other unconfirmed notifications
    const hasUnconfirmedNotifications = useAppSelector(
        (state: any) => state.notification.unconfirmedCount > 0
    );

    const hasNotificationRelatedActions = hasUnconfirmedNotifications || notifications?.length > 0;

    const canAssignOrderForMyself = order?.state <= TransportOrderState.PickedUp &&
        order?.employeeId === null;
    const canRemoveAssigment = order?.state <= TransportOrderState.PickedUp &&
        order?.employeeId === employeeId;
    const canEditAndDeleteOrder = canCreateWorkOrderInMobile &&
        order?.state === TransportOrderState.OrderCreated &&
        order?.createdEmployeeId === employeeId;

    useEffect(() => {
        if (hasUnconfirmedNotifications && notifications?.length > 0) {
            setBottomSpace(122);
        } else {
            setBottomSpace(72);
        }
    }, [hasNotificationRelatedActions]);

    const handleEditOrder = (fields: Partial<TransportOrderDetailsDto>) => {
        dispatch(updateEditingOrder(fields));
    };

    const handleSave: HandleSaveProps = async (
        updatedFields = {},
        location = undefined
    ) => {
        try {
            if (!editOrder) return;
            const orderToSave = toUpdateDto(editOrder, updatedFields, location);
            await dispatch(saveTransportOrderWithNotification({ updateDto: orderToSave, orderId: editOrder.id })).unwrap();
            dispatch(addSuccessMsg(t("transport.order.saveSuccess")));
        } catch (error) {
            dispatch(addErrorMsg({ message: t("transport.order.saveFailed") }));
        }
    };

    const handleStateChange = (newState, location) => {
        void handleSave({ state: newState }, location);
    };

    const handleCommentsChange = (comments) => {
        void handleSave({ driverComments: comments });
    };

    const handleEmployeeSave: HandleSaveProps = async (updatedFields = {}) => {
        try {
            if (!editOrder) return;
            await dispatch(saveTransportOrderEmployee({ orderId: editOrder.id, employeeId: updatedFields.employeeId })).unwrap();
            if (updatedFields.employeeId === null) {
                dispatch(addSuccessMsg(t("transport.order.unassignSuccess")));
            } else {
                dispatch(addSuccessMsg(t("transport.order.assignToMeSuccess")));
            }
        } catch (error) {
            dispatch(addErrorMsg({ message: t("transport.order.assignChangeFailed") }));
        }
    };

    const handleDelete = (id: string) => {
        confirm({
            icon: DeleteForeverIcon,
            title: t("transport.order.deleteConfirmation"),
            submitText: t("common.continue"),
            cancelText: t("common.cancel"),
            type: ConfirmType.Remove,
            onSubmit: () => {
                dispatch(removeTransportOrder(id))
                    .unwrap()
                    .then(() => {
                        void navigate(generatePath("/transport/orders"));
                        dispatch(
                            addSuccessMsg(
                                t("transport.order.deleteSuccessful")
                            )
                        );
                    }
                    )
                    .catch(handleApiError);
            },
        });
    };

    const handleEmployeeChange = () => {
        void handleEmployeeSave({ employeeId: employeeId });
    };

    const handleRemoveAssigment = () => {
        void handleEmployeeSave({ employeeId: null });
    };

    const onDelete = () => handleDelete(order.id);

    const handleConfirmNotifications = async () => {
        if (!notifications?.length) return;
        try {
            const updatedNotifications = await confirmDesktopToMobileNotifications(orderId, employeeId);
            const hasUnconfirmed = updatedNotifications.some(
                (notification) => notification.state < TransportOrderNotificationState.Confirmed
            );
            dispatch(addSuccessMsg(t("notifications.notificationsConfirmed"), 5000));
            navigate(hasUnconfirmed ? "/notifications" : "/");
        } catch (error) {
            console.error("Error confirming notifications:", error);
            dispatch(addErrorMsg({ message: t("notifications.notificationConfirmationFailed") }));
        }
    };

    return (
        <MainLayout
            topComponent={<PageTitle header={t("transport.order.orderDetails")} />}
        >
            {loading || !order || !editOrder ? (
                <LoadingIndicator />
            ) : (
                <>
                    {(canAssignOrderForMyself || canRemoveAssigment || canEditAndDeleteOrder) && (
                        <Box
                            mb={4}
                            sx={{ display: "flex", justifyContent: "center" }}
                        >
                            {canAssignOrderForMyself && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    sx={{ maxWidth: "360px" }}
                                    onClick={handleEmployeeChange}
                                >
                                    {t("transport.actions.assignToMe")}
                                </Button>
                            )}
                            {canRemoveAssigment && (
                                <Button
                                    variant="contained"
                                    color="info"
                                    sx={{ maxWidth: "360px" }}
                                    onClick={handleRemoveAssigment}
                                >
                                    {t("transport.actions.removeAssigment")}
                                </Button>
                            )}
                            {canEditAndDeleteOrder && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        marginLeft: 4
                                    }}
                                >
                                    <LinkIconButton
                                        to={generatePath(
                                            "/transport/order/edit/:orderId",
                                            {
                                                orderId: order.id,
                                            }
                                        )}
                                    >
                                        <EditIcon color="primary" />
                                    </LinkIconButton>
                                    <DeleteForeverIcon
                                        color="error"
                                        onClick={onDelete}
                                        sx={{ marginLeft: 1 }}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                    <OrderDetails order={order} />
                    {localOrderState !== null && (
                        <Box sx={{ mx: 2 }}>
                            <OrderStateToggle
                                order={editOrder}
                                onStateChange={handleStateChange}
                                setEditOrder={handleEditOrder}
                                disabled={editDisabledState}
                            />
                        </Box>
                    )}
                    <ProductTable
                        changesDisabled={
                            editDisabledState ||
                            order?.invoicingState === TransportOrderInvoicingState.InvoicedInternally ||
                            order?.invoicingState === TransportOrderInvoicingState.InvoicedExternally
                        }
                    />
                    <OrderAttachments employeeId={employeeId} />
                    {order?.transportDetails && <TransportDetails order={order} />}
                    <DriverComments
                        onCommentsChange={handleCommentsChange}
                        setEditOrder={handleEditOrder}
                    />
                    {order?.orderTypeId && <OrderType order={order} />}
                </>
            )}

            <Box sx={{ height: bottomSpace }} />

            {hasNotificationRelatedActions && (
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 16,
                        left: 16,
                        right: 16,
                        width: "calc(100% - 32px)",
                    }}
                >
                    {hasUnconfirmedNotifications && (
                        <Button
                            variant="contained"
                            color="info"
                            sx={{
                                height: 56,
                                fontSize: 18,
                                fontWeight: "bold",
                                width: "100%",
                            }}
                            onClick={() => { navigate("/notifications"); }}
                        >
                            {t("notifications.greeting")}
                        </Button>
                    )}
                    {notifications?.length > 0 && (
                        <Button
                            variant="contained"
                            color="success"
                            sx={{
                                height: 56,
                                fontSize: 18,
                                fontWeight: "bold",
                                width: "100%",
                                mt: 1
                            }}
                            onClick={() => {
                                handleConfirmNotifications()
                                    .catch((e) => console.error(e));
                            }}
                        >
                            {t("notifications.confirmNotifications")}
                        </Button>
                    )}
                </Box>
            )}
        </MainLayout>
    );
};
